import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { PlusCircle, History, Send, Trash2, FileSpreadsheet, FileText } from 'lucide-react';
import { useIAChat } from '../../contexts/IAChatContext.jsx';
import ChartRenderer from './ChartRenderer.jsx';
import { exportarExcel, exportarPdf } from './exportResultado.js';
import { formatCellValue, formatRowsForExport } from './formatCell.js';
import RespuestaMarkdown from './RespuestaMarkdown.jsx';

/**
 * UI compartida de mensajes/input — puerto Tailwind de
 * `IbuFront/src/components/ia/ChatPanel.jsx` (V1, que usaba reactstrap). Usada tanto
 * por el widget flotante (`ChatWidget.jsx`) como por la página dedicada
 * (`pages/asistente-ia/AsistenteIAPage.jsx`), igual que en V1.
 *
 * Incluye los botones Excel/PDF por bloque de resultados y el formato de celdas
 * (fechas dd/mm/yyyy, dinero con $) de V1 (`exportResultado.js` / `formatCell.js`).
 */

// El backend guarda un bloque de resultados por cada consulta que hizo la IA en el
// turno (ver chat.service.js `resultados`/`rawData`) — mismo formato que V1.
function normalizarBloques(rawData) {
  if (!Array.isArray(rawData) || rawData.length === 0) return [];
  const esFormatoNuevo = rawData[0] && typeof rawData[0] === 'object' && Array.isArray(rawData[0].rows);
  if (esFormatoNuevo) return rawData;
  return [{ description: null, rows: rawData }];
}

function ResultadoTabla({ description, rows }) {
  if (!rows?.length) return null;
  const columnas = Object.keys(rows[0]);
  const filas = rows.slice(0, 50);
  // Excel limita el nombre de hoja a 31 caracteres y no acepta símbolos especiales.
  const nombreExport = description ? description.replace(/[^\w\s-]/g, '').trim().slice(0, 28) || 'asistente_ia' : 'asistente_ia';

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        {description ? <span className="text-xs text-slate-500">{description}</span> : <span />}
        <div className="flex gap-2 ml-auto">
          <button type="button" className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1" onClick={() => exportarExcel(formatRowsForExport(rows), nombreExport)}>
            <FileSpreadsheet size={13} /> Excel
          </button>
          <button type="button" className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1" onClick={() => exportarPdf(formatRowsForExport(rows), nombreExport, description || undefined)}>
            <FileText size={13} /> PDF
          </button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-64 rounded border border-slate-200">
        <table className="min-w-full text-xs bg-white">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              {columnas.map((c) => (
                <th key={c} className="px-2 py-1 text-left font-medium text-slate-600 whitespace-nowrap border-b border-slate-200">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                {columnas.map((c) => (
                  <td key={c} className="px-2 py-1 whitespace-nowrap text-slate-700">
                    {formatCellValue(row[c], c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[92%] rounded-lg px-3 py-2 text-sm ${isUser ? 'whitespace-pre-wrap' : ''} ${
          isUser ? 'bg-primary-600 text-white' : msg.error ? 'bg-danger-50 text-danger-800' : 'bg-slate-100 text-slate-800'
        }`}
      >
        {isUser ? msg.text : <RespuestaMarkdown texto={msg.text} />}
        {(msg.charts || []).map((chart, i) =>
          chart?.data?.length > 0 ? (
            <div key={i} className="mt-3 p-2 bg-white rounded">
              <ChartRenderer chart={chart} />
            </div>
          ) : null
        )}
        {normalizarBloques(msg.rawData).map((bloque, i) => (
          <ResultadoTabla key={i} description={bloque.description} rows={bloque.rows} />
        ))}
      </div>
    </div>
  );
}

export default function ChatPanel({ onShowHistory }) {
  const { messages, loading, loadingConv, enviar, activeConv, nuevaConversacion, eliminarConversacion } = useIAChat();
  const [texto, setTexto] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const onSubmit = (e) => {
    e.preventDefault();
    const t = texto;
    if (!t.trim() || loading) return;
    setTexto('');
    enviar(t);
  };

  const onEliminar = async () => {
    if (!activeConv) return;
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar la conversación "${activeConv.titulo}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;
    eliminarConversacion(activeConv._id);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 flex-shrink-0">
        <h3 className="text-sm font-semibold truncate text-slate-800">{activeConv?.titulo || 'Asistente IA'}</h3>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={nuevaConversacion} title="Nuevo chat" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50">
            <PlusCircle size={18} />
          </button>
          {onShowHistory && (
            <button type="button" onClick={onShowHistory} title="Historial" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50">
              <History size={18} />
            </button>
          )}
          {activeConv && (
            <button type="button" onClick={onEliminar} title="Eliminar conversación" className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-grow overflow-auto px-3 py-3" style={{ minHeight: 200 }}>
        {loadingConv && <p className="text-center text-slate-400 text-sm mt-4">Cargando conversación…</p>}
        {!loadingConv && messages.length === 0 && (
          <p className="text-center text-slate-400 text-sm mt-4">
            Pregúntame algo sobre contenedores, clientes, proveedores, cargos, gastos o el reparto a socios.
          </p>
        )}
        {!loadingConv && messages.map((m) => <Message key={m.id} msg={m} />)}
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="px-3 py-2 rounded bg-slate-100 text-sm text-slate-400">Escribiendo…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-slate-200 p-2 flex-shrink-0">
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe tu pregunta…"
          disabled={loading}
          className="input-base flex-1"
        />
        <button
          type="submit"
          disabled={loading || !texto.trim()}
          className="btn-primary p-2"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
